import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Building2, 
  Loader2,
  ChevronRight,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  MapPin,
  FileText,
  Eye
} from 'lucide-react';

const PropertiesPage = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/properties');
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { className: 'bg-muted text-muted-foreground border-muted', label: 'Borrador' },
      submitted: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Enviado' },
      initial_review: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Revisión Inicial' },
      missing_info: { className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', label: 'Info Faltante' },
      legal_review: { className: 'bg-purple-500/10 text-purple-500 border-purple-500/20', label: 'Revisión Legal' },
      technical_review: { className: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20', label: 'Revisión Técnica' },
      financial_structuring: { className: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20', label: 'Estructuración' },
      approved: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Aprobado' },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado' },
      in_negotiation: { className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', label: 'En Negociación' },
      contracting: { className: 'bg-teal-500/10 text-teal-500 border-teal-500/20', label: 'Contratando' },
      closed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Cerrado' },
    };
    const style = styles[status] || styles.draft;
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      residential: 'Residencial',
      commercial: 'Comercial',
      industrial: 'Industrial',
      land: 'Terreno',
      mixed_use: 'Uso Mixto',
      office: 'Oficinas',
      retail: 'Retail',
      hotel: 'Hotelería',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">Mis Inmuebles</h1>
          <p className="body-text">
            Administre sus propiedades propuestas para inversión.
          </p>
        </div>
        <Link to="/app/properties/new">
          <Button className="btn-primary gap-2" data-testid="new-property-btn">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Proponer Inmueble
          </Button>
        </Link>
      </div>

      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          El proceso de evaluación de propiedades incluye múltiples etapas: revisión inicial, due diligence legal, análisis técnico y estructuración financiera. Le mantendremos informado del avance.
        </AlertDescription>
      </Alert>

      {/* Properties List */}
      {properties.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <h3 className="text-lg font-medium mb-2">No ha propuesto inmuebles aún</h3>
            <p className="text-muted-foreground mb-6">
              ¿Tiene una propiedad que podría interesar a nuestros inversionistas?
            </p>
            <Link to="/app/properties/new">
              <Button className="btn-primary">Proponer un Inmueble</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {properties.map((property) => (
            <Card key={property.property_id} className="card-dashboard hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(property.status)}
                      <span className="text-xs text-muted-foreground">
                        {new Date(property.created_at).toLocaleDateString('es-MX')}
                      </span>
                    </div>
                    <h3 className="font-medium mb-2">{property.title}</h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" strokeWidth={1.5} />
                        {property.city}, {property.state}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" strokeWidth={1.5} />
                        {getPropertyTypeLabel(property.property_type)}
                      </span>
                      {property.land_area_m2 && (
                        <span>{property.land_area_m2.toLocaleString()} m²</span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Link to={`/app/properties/${property.property_id}`}>
                      <Button variant="outline" size="sm" className="gap-2">
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                        Ver Detalles
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Status notes */}
                {property.status_notes && (
                  <Alert className="mt-4 border-muted">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      <strong>Nota del revisor:</strong> {property.status_notes}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Process Info */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg">Proceso de Evaluación</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { label: 'Enviado', desc: 'Recibido' },
              { label: 'Revisión Inicial', desc: 'Análisis preliminar' },
              { label: 'Revisión Legal', desc: 'Due diligence' },
              { label: 'Revisión Técnica', desc: 'Valuación' },
              { label: 'Estructuración', desc: 'Términos' },
              { label: 'Aprobado', desc: 'Listo' },
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-8 h-8 mx-auto mb-2 rounded-full border-2 border-muted flex items-center justify-center text-sm text-muted-foreground">
                  {index + 1}
                </div>
                <p className="text-xs font-medium">{step.label}</p>
                <p className="text-xs text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertiesPage;
